/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AdyenCheckout, Ach, type AchConfiguration } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  Alert,
  AlertTypography,
  AppHeaderBackButton,
  Box,
  Checkbox,
  CheckboxGroup,
  CheckboxTypography,
  Icon,
  Link,
  Modal,
  ModalActionButton,
  ModalActions,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
  StickyButton,
  Typography,
} from '@ace-clublabs/shared-component-library';
import LogCannon from '@ace-clublabs/log-cannon';
import {
  ErrorCode,
  IConfigEntry,
  IProcessPaymentWithManifestRequest,
  IAdyenEncryptedAchData,
  PaymentType,
} from '@ace-clublabs/payment-types';
import { useNavigate } from 'react-router-dom';
import echeckImage from '../assets/echeck-find.png';
import { PaymentReview } from '../containers/PaymentReview';
import { Pages } from '../shared/pages';
import { useGlobalState } from '../shared/state';
import { ErrorSeverity, ErrorType } from '../shared/types';
import { getTermsUrl } from '../utils/methods';
import { ProcessPaymentHelper } from '../utils/processPaymentHelper';
import UrlHelper from '../utils/urlHelper';
import Header from './Header';
import PaymentHeader from './PaymentHeader';
import { PaymentMethodsAuthorizationModal } from './PaymentMethodsAuthorizationModal';
import '../styles/override.css';
import { sources } from '../api/sources';
import { IGetAdyenSession } from '../api/contracts';
import {
  ACH_BLUR_FIELD_NAMES,
  type AchBlurFieldId,
  eCheckAchFieldBlurMessage,
} from '../utils/eCheckFieldValidators';

export interface ECheckFormProps {
  manifestId: string;
  amount: number;
  returnUrl: string;
  isScheduled: boolean | undefined;
  autoPay?: boolean;
  appId?: string;
  scheduledDate?: string;
  paymentFormSubtitle?: string;
  setParentProcessing?: any;
  hideWhileParentProcessing?: boolean;
}

/** Shown on payment review when routing is not available from the SDK. */
const ROUTING_REVIEW_PLACEHOLDER = '·········';

/**
 * Adyen Web ACH uses one `selectedAccountType` value (`personal.checking`, etc.) for both
 * account holder type and bank account type. Types only document `ownerName` prefill, but the
 * runtime merges this into form state. We hide the selector in CSS and default here.
 */
const ADYEN_ACH_DEFAULT_SELECTED_ACCOUNT_TYPE = 'personal.checking';

/** Adyen ACH `Field` label `data-id` values — insert “Where do I find this?” after these only. */
const ACH_HELP_FIELD_NAMES: readonly AchBlurFieldId[] = ['routingNumber', 'accountNumber'];

const ACH_REQUIRED_HOST_ID: Record<AchBlurFieldId, string> = {
  ownerName: 'echeck-adyen-required-host-ownerName',
  routingNumber: 'echeck-adyen-required-host-routingNumber',
  accountNumber: 'echeck-adyen-required-host-accountNumber',
  accountNumberVerification: 'echeck-adyen-required-host-accountNumberVerification',
};

const ACH_REQUIRED_ERROR_HOST_CLASS = 'echeck-adyen-required-error-host';

function achPayloadFromPaymentMethod(pm: any): IAdyenEncryptedAchData | null {
  if (!pm) return null;
  const encryptedBankAccountNumber = pm.encryptedBankAccountNumber ?? pm.bankAccountNumber;
  const encryptedBankLocationId = pm.encryptedBankLocationId ?? pm.bankLocationId;
  if (!encryptedBankAccountNumber || !encryptedBankLocationId) return null;

  return {
    ownerName: String(pm.ownerName || '').trim(),
    encryptedBankAccountNumber: String(encryptedBankAccountNumber),
    encryptedBankLocationId: String(encryptedBankLocationId),
    bankAccountType: String(pm.bankAccountType || 'checking'),
    accountHolderType: String(pm.accountHolderType || 'personal'),
    last4: pm.bankAccountNumberLastDigits || pm.lastFourDigits || pm.last4,
  };
}

/**
 * E-check via Adyen Web `Ach` component (session checkout).
 * Requires the Adyen session to be created with ACH available as a payment method.
 */
const ECheckFormAdyen: React.FC<ECheckFormProps> = (props) => {
  const navigate = useNavigate();
  const {
    manifestId,
    amount,
    returnUrl,
    autoPay,
    appId,
    scheduledDate,
    isScheduled,
    setParentProcessing,
    hideWhileParentProcessing = false,
  } = props;

  const [manifest] = useGlobalState('manifest');
  const [redirectMap, setRedirected] = useGlobalState('redirectMap');
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [paymentMethodsSkipped, setpaymentMethodsSkipped] = useGlobalState('paymentMethodsSkipped');
  const [, setError] = useGlobalState('error') || [];
  const [adyenSession, setAdyenSession] = useState<IGetAdyenSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isProcessing, setProcessing] = useState(false);
  const [isPaymentReview, setPaymentReview] = useState(false);
  const [last4, setLast4] = useState('');
  const [routingNumber, setRoutingNumber] = useState(ROUTING_REVIEW_PLACEHOLDER);
  const [showModal, setShowModal] = useState(false);
  const [showEcheckModal, setShowEcheckModal] = useState(false);
  const config = manifest?.clientConfig || ({} as IConfigEntry);
  const [storePaymentChecked, setStorePaymentChecked] = useState(false);
  const [reviewPayload, setReviewPayload] = useState<any>(undefined);
  const [showPaymentAuthorization, setShowPaymentAuthorization] = useState(false);
  const [achComponent, setAchComponent] = useState<any>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [capturedAchData, setCapturedAchData] = useState<IAdyenEncryptedAchData | null>(null);
  const [achApiError, setAchApiError] = useState('');

  const adyenInitializedRef = useRef(false);
  const latestAchStateRef = useRef<any>(null);

  const [achRequiredTouched, setAchRequiredTouched] = useState<Record<AchBlurFieldId, boolean>>({
    ownerName: false,
    routingNumber: false,
    accountNumber: false,
    accountNumberVerification: false,
  });
  const [achRequiredErrors, setAchRequiredErrors] = useState<Record<AchBlurFieldId, string | null>>({
    ownerName: null,
    routingNumber: null,
    accountNumber: null,
    accountNumberVerification: null,
  });
  const [achRequiredPortalHosts, setAchRequiredPortalHosts] = useState<Record<AchBlurFieldId, HTMLElement | null>>({
    ownerName: null,
    routingNumber: null,
    accountNumber: null,
    accountNumberVerification: null,
  });

  const touchAndValidateAchBlur = useCallback((field: AchBlurFieldId) => {
    setAchRequiredTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    setAchRequiredErrors((prev) => ({
      ...prev,
      [field]: eCheckAchFieldBlurMessage(field, latestAchStateRef.current),
    }));
  }, []);

  const getFocusedAchBlurField = useCallback((): AchBlurFieldId | null => {
    const focusedLabel = document.querySelector('#ach-container .adyen-checkout__label--focused');
    if (!focusedLabel) return null;
    const text = focusedLabel.querySelector('span.adyen-checkout__label__text[data-id]');
    const id = text?.getAttribute('data-id');
    if (
      id === 'ownerName' ||
      id === 'routingNumber' ||
      id === 'accountNumber' ||
      id === 'accountNumberVerification'
    ) {
      return id;
    }
    return null;
  }, []);

  useLayoutEffect(() => {
    if (!achComponent) return;
    const container = document.getElementById('ach-container');
    if (!container) return;

    /** Keep help under validation copy: input → contextual text (errors/hints) → “Where do I find this?” */
    const positionAchFieldHelp = (field: Element, wrap: Element) => {
      const inputWrapper = field.querySelector('.adyen-checkout__input-wrapper');
      if (!inputWrapper) {
        field.appendChild(wrap);
        return;
      }
      const contextuals = [...field.querySelectorAll('.adyen-checkout-contextual-text')].filter(
        (el) => inputWrapper.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
      );
      const host = field.querySelector(`.${ACH_REQUIRED_ERROR_HOST_CLASS}`);
      const anchor =
        contextuals.length > 0 ? contextuals[contextuals.length - 1]! : host ?? inputWrapper;
      if (wrap.parentElement === anchor.parentElement && wrap.previousElementSibling === anchor) return;
      anchor.insertAdjacentElement('afterend', wrap);
    };

    const ensureRequiredErrorHost = (field: Element, name: AchBlurFieldId) => {
      const id = ACH_REQUIRED_HOST_ID[name];
      if (field.querySelector(`#${id}`)) return;
      const inputWrapper = field.querySelector('.adyen-checkout__input-wrapper');
      const host = document.createElement('div');
      host.id = id;
      host.className = ACH_REQUIRED_ERROR_HOST_CLASS;
      if (inputWrapper) inputWrapper.insertAdjacentElement('afterend', host);
      else field.appendChild(host);
    };

    const repositionAchHelps = () => {
      for (const name of ACH_HELP_FIELD_NAMES) {
        const label = container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`);
        const field = label?.closest('.adyen-checkout__field');
        const wrap = field?.querySelector('.echeck-adyen-field-help');
        if (field && wrap) positionAchFieldHelp(field, wrap);
      }
    };

    const attachHelpLinks = () => {
      container.querySelectorAll('.echeck-adyen-field-help').forEach((el) => el.remove());
      for (const name of ACH_BLUR_FIELD_NAMES) {
        const label = container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`);
        const field = label?.closest('.adyen-checkout__field');
        if (!field) continue;
        ensureRequiredErrorHost(field, name);
      }
      for (const name of ACH_HELP_FIELD_NAMES) {
        const label = container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`);
        const field = label?.closest('.adyen-checkout__field');
        if (!field) continue;
        const wrap = document.createElement('div');
        wrap.className = 'echeck-adyen-field-help';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.id =
          name === 'routingNumber' ? 'echeck-adyen-routing-help-link' : 'echeck-adyen-account-help-link';
        btn.className = 'echeck-adyen-field-help__btn';
        btn.textContent = 'Where do I find this?';
        btn.addEventListener('click', (ev) => {
          ev.preventDefault();
          setShowEcheckModal(true);
        });
        wrap.appendChild(btn);
        /* Inside the field so `.adyen-checkout__fieldset__fields` row-gap does not treat the link as its own row. */
        positionAchFieldHelp(field, wrap);
      }
      setAchRequiredPortalHosts({
        ownerName: document.getElementById(ACH_REQUIRED_HOST_ID.ownerName),
        routingNumber: document.getElementById(ACH_REQUIRED_HOST_ID.routingNumber),
        accountNumber: document.getElementById(ACH_REQUIRED_HOST_ID.accountNumber),
        accountNumberVerification: document.getElementById(
          ACH_REQUIRED_HOST_ID.accountNumberVerification,
        ),
      });
    };

    const fieldsPresent = () =>
      ACH_BLUR_FIELD_NAMES.every((name) =>
        container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`),
      );

    const cleanupHelps = () => {
      container.querySelectorAll('.echeck-adyen-field-help').forEach((el) => el.remove());
    };

    let layoutMo: MutationObserver | null = null;
    const startLayoutObserver = () => {
      if (layoutMo) return;
      layoutMo = new MutationObserver(() => {
        repositionAchHelps();
      });
      layoutMo.observe(container, { childList: true, subtree: true });
    };
    const stopLayoutObserver = () => {
      layoutMo?.disconnect();
      layoutMo = null;
    };

    if (fieldsPresent()) {
      attachHelpLinks();
      startLayoutObserver();
      return () => {
        stopLayoutObserver();
        cleanupHelps();
      };
    }

    const mo = new MutationObserver(() => {
      if (fieldsPresent()) {
        attachHelpLinks();
        startLayoutObserver();
        mo.disconnect();
      }
    });
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      stopLayoutObserver();
      cleanupHelps();
    };
  }, [achComponent]);

  const allowStoredPayment = config?.allowStoredPayment;

  const adyenSessionConfig = useMemo(() => {
    return adyenSession
      ? {
        id: adyenSession.adyen.sessionId,
        sessionData: adyenSession.adyen.sessionData,
      }
      : undefined;
  }, [adyenSession?.adyen.sessionId, adyenSession?.adyen.sessionData]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setIsLoadingSession(true);
        const sessionData = await sources.getAdyenSession(manifestId);
        if (isMounted) setAdyenSession(sessionData);
      } catch (e) {
        LogCannon.blast('ERROR: Failed to fetch Adyen session (e-check)', { e, manifestId });
        if (isMounted) {
          setParentProcessing?.({ status: false });
          navigate(Pages.Error);
        }
      } finally {
        if (isMounted) setIsLoadingSession(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [manifestId, navigate, setParentProcessing]);

  useEffect(() => {
    adyenInitializedRef.current = false;
  }, [adyenSessionConfig]);

  useEffect(() => {
    if (!adyenSessionConfig || !adyenSession) return;
    if (adyenInitializedRef.current) return;
    adyenInitializedRef.current = true;

    let isMounted = true;
    let ach: any = null;

    const init = async () => {
      try {
        let loadingHidden = false;
        const hideLoading = () => {
          if (!loadingHidden && isMounted) {
            loadingHidden = true;
            setTimeout(() => {
              if (isMounted) {
                setParentProcessing?.({ status: false });
              }
            }, 500);
          }
        };

        const achTranslations = {
          'ach.accountHolderNameField.title': 'Name on the account',
          'ach.bankAccountNumberVerification.label': 'Re-type account number',
        };
        const globalConfiguration = {
          session: adyenSessionConfig,
          environment: adyenSession.adyen.environment,
          amount: { value: amount, currency: 'USD' },
          locale: 'en-US',
          countryCode: 'US',
          clientKey: adyenSession.adyen.clientKey,
          showPayButton: false,
          translations: {
            'en-US': achTranslations,
            en_US: achTranslations,
            en: achTranslations,
          },
          onError: (error: any, component: any) => {
            LogCannon.blast('ERROR: Adyen ACH checkout error', { error, component, manifestId });
            setParentProcessing?.({ status: false });
            navigate(Pages.Error);
          },
        };

        const achConfiguration = {
          showPayButton: false,
          hasHolderName: true,
          enableStoreDetails: false,
          data: {
            selectedAccountType: ADYEN_ACH_DEFAULT_SELECTED_ACCOUNT_TYPE,
          } as AchConfiguration['data'],
          placeholders: {
            ownerName: 'Name on the account',
            routingNumber: 'Routing number',
            accountNumber: 'Account number',
            accountNumberVerification: 'Re-type account number',
          },
          amount: { value: 0, currency: 'USD' },
          onChange: (state: any) => {
            hideLoading();
            latestAchStateRef.current = state;
            setAchApiError('');
            setAchRequiredErrors((prev) => {
              const next = { ...prev };
              for (const f of ACH_BLUR_FIELD_NAMES) {
                if (eCheckAchFieldBlurMessage(f, state) === null) next[f] = null;
              }
              return next;
            });
            const valid = !!state?.isValid;
            setIsFormValid(valid);
            const pm = state?.data?.paymentMethod;
            if (valid && pm) {
              const payload = achPayloadFromPaymentMethod(pm);
              if (payload?.ownerName) {
                setCapturedAchData(payload);
                if (payload.encryptedBankAccountNumber) setLast4(String(payload.encryptedBankAccountNumber.substring(payload.encryptedBankAccountNumber.length - 4)));
                if (payload.encryptedBankLocationId) setRoutingNumber(String(payload.encryptedBankLocationId));
              }
            } else {
              setCapturedAchData(null);
            }
          },
          styles: {
            base: {
              color: '#3D4043',
              fontSize: '16px',
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: '400',
            },
            error: { color: '#BE3317' },
          },
        };

        const checkout = await AdyenCheckout(globalConfiguration);
        if (!isMounted) return;
        ach = new Ach(checkout, achConfiguration).mount('#ach-container');
        setAchComponent(ach);
      } catch (error) {
        LogCannon.blast('ERROR: Failed to initialize Adyen ACH form', { error, manifestId });
        if (isMounted) navigate(Pages.Error);
      }
    };

    init();
    return () => {
      isMounted = false;
      if (ach) {
        try {
          ach.unmount?.();
        } catch (e) {
          console.warn('Error unmounting Adyen ACH', e);
        }
      }
    };
  }, [adyenSessionConfig, adyenSession, amount, manifestId, navigate, setParentProcessing]);

  useEffect(() => {
    ACH_BLUR_FIELD_NAMES.forEach((name) => {
      const label = document.querySelector(`#ach-container span.adyen-checkout__label__text[data-id="${name}"]`);
      const el = label?.closest('.adyen-checkout__field') as HTMLElement | null;
      if (!el) return;
      if (achRequiredTouched[name] && achRequiredErrors[name]) el.classList.add('fp-required-error');
      else el.classList.remove('fp-required-error');
    });
  }, [achComponent, achRequiredTouched, achRequiredErrors]);

  useEffect(() => {
    if (!achComponent) return;

    let cancelled = false;
    const timeouts: number[] = [];
    const cleanups: Array<() => void> = [];

    const attachFocusOut = (fieldKey: AchBlurFieldId) => {
      const label = document.querySelector(
        `#ach-container span.adyen-checkout__label__text[data-id="${fieldKey}"]`,
      );
      const el = label?.closest('.adyen-checkout__field') as HTMLElement | null;
      if (!el) return;

      const onFocusOut = () => {
        if (cancelled) return;
        const t = window.setTimeout(() => {
          if (cancelled) return;
          const active = document.activeElement as HTMLElement | null;
          if (active && el.contains(active)) {
            const iw = el.querySelector('.adyen-checkout__input-wrapper');
            if (iw && (iw === active || iw.contains(active))) return;
          }
          touchAndValidateAchBlur(fieldKey);
        }, 0);
        timeouts.push(t);
      };

      el.addEventListener('focusout', onFocusOut);
      cleanups.push(() => el.removeEventListener('focusout', onFocusOut));
    };

    const previouslyFocused = new Set<AchBlurFieldId>();

    const observer = new MutationObserver((mutations) => {
      if (cancelled) return;
      const hasFocusClassChange = mutations.some((mutation) => {
        const target = mutation.target as HTMLElement;
        return target.classList?.contains('adyen-checkout__label') || !!target.closest?.('.adyen-checkout__label');
      });
      if (!hasFocusClassChange) return;

      const current = getFocusedAchBlurField();
      previouslyFocused.forEach((key) => {
        if (key !== current) {
          touchAndValidateAchBlur(key);
          previouslyFocused.delete(key);
        }
      });
      if (current && !previouslyFocused.has(current)) previouslyFocused.add(current);
    });

    let focusListenersAttached = false;
    const tryFind = () => {
      if (cancelled || focusListenersAttached) return;
      const allPresent = ACH_BLUR_FIELD_NAMES.every((name) =>
        document
          .querySelector(`#ach-container span.adyen-checkout__label__text[data-id="${name}"]`)
          ?.closest('.adyen-checkout__field'),
      );
      if (allPresent) {
        focusListenersAttached = true;
        ACH_BLUR_FIELD_NAMES.forEach((key) => attachFocusOut(key));
        const c = document.getElementById('ach-container');
        if (c) {
          observer.observe(c, { attributes: true, attributeFilter: ['class'], subtree: true });
          cleanups.push(() => observer.disconnect());
        }
        return;
      }
      timeouts.push(window.setTimeout(tryFind, 50));
    };

    tryFind();

    return () => {
      cancelled = true;
      timeouts.forEach((id) => clearTimeout(id));
      cleanups.forEach((fn) => fn());
    };
  }, [achComponent, touchAndValidateAchBlur, getFocusedAchBlurField]);

  const redirect = (url: string) => {
    setRedirected({ ...redirectMap, [manifestId]: url });
    navigate(`/redirect/${manifestId}`);
  };

  const handleSoftError = (error: { code: ErrorCode }) => { // to do: confirm soft errors are properly displayed once we have the api done.
    switch (error.code) {
      case ErrorCode.InvalidRoutingNumber:
        setAchApiError('Invalid routing number.');
        break;
      case ErrorCode.InvalidAccountNumber:
        setAchApiError('Invalid account number.');
        break;
      case ErrorCode.StorePaymentError:
        setAchApiError('This account number is invalid. Please try another payment method.');
        break;
      case ErrorCode.NetworkTimeout:
        if (appId === 'manage-saved-payments') {
          setAchApiError('Your payment method could not be saved. Please try again.');
        } else {
          setShowModal(true);
        }
        break;
      default:
        setShowModal(true);
        break;
    }
  };

  const submitPayment = async (payload: IAdyenEncryptedAchData) => {
    try {
      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: false,
      });
      setProcessing(true);

      const sessionId = adyenSessionConfig?.id || '';
      const processPaymentBody: IProcessPaymentWithManifestRequest = {
        manifestId,
        paymentType: PaymentType.Echeck,
        scheduledDate,
        adyenData: {
          sessionId,
          achData: {
            ownerName: payload.ownerName,
            encryptedBankAccountNumber: payload.encryptedBankAccountNumber,
            encryptedBankLocationId: payload.encryptedBankLocationId,
            bankAccountType: payload.bankAccountType,
            accountHolderType: payload.accountHolderType,
            last4: payload.encryptedBankAccountNumber.substring(payload.encryptedBankAccountNumber.length - 4),
          },
        },
      };

      if ((allowStoredPayment && storePaymentChecked) || appId === 'manage-saved-payments') {
        processPaymentBody.storePayment = { enable: true };
      }

      setPaymentProcessing(true);
      const { transactionId } = await ProcessPaymentHelper.processPayment(processPaymentBody);
      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          const returnUrlWithTransactionId = UrlHelper.addQueryParam(returnUrl, { transactionId });
          redirect(returnUrlWithTransactionId);
        },
      });
    } catch (error: any) {
      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          setProcessing(false);
        },
      });
      if (error.type === ErrorSeverity.SOFT) {
        setProcessing(false);
        setPaymentReview(false);
        handleSoftError(error);
      } else {
        const errorMessage = 'ERROR: Submit Adyen ECheck Payment Exception';
        if (typeof error === 'string') {
          LogCannon.blast(`${errorMessage}: ${error}`);
        } else {
          LogCannon.blast(errorMessage, error as object);
        }
        if (error.code === ErrorCode.TooManyRequests) {
          setError?.({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
        }
        navigate(Pages.Error);
      }
    }
  };

  const renderPaymentReview = manifest?.clientConfig?.renderPaymentReview;

  const handleContinue = () => {
    const state = achComponent?.state ?? latestAchStateRef.current;
    if (!state?.isValid) return;
    const payload = capturedAchData || achPayloadFromPaymentMethod(state?.data?.paymentMethod);
    if (!payload) return;
    setReviewPayload(payload);

    if (renderPaymentReview && amount > 0) {
      setPaymentReview(true);
    } else {
      void submitPayment(payload);
    }
  };

  const modalDialog = (
    <Modal
      open={showModal && !isProcessing}
      onClose={() => setShowModal(false)}
      variants={{ xs: 'bottom-sheet', sm: 'regular' }}
    >
      <ModalTitle id="error-modal-title-echeck-adyen" sx={{ marginRight: '2rem' }}>
        Please try again.
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowModal(false)} />
      </ModalTitle>
      <ModalContent id="error-modal-content-echeck-adyen">
        <Typography variant="b1Regular">There was an unexpected error.</Typography>
      </ModalContent>
      <ModalActions
        actionLayout={{ sm: 'fullWidth', md: 'fullWidth', lg: 'rightAlign', xl: 'rightAlign' }}
      >
        <ModalActionButton label="OK" onClick={() => setShowModal(false)} />
      </ModalActions>
    </Modal>
  );

  const echeckModal = (
    <Modal
      open={showEcheckModal}
      onClose={() => setShowEcheckModal(false)}
      variants={{ xs: 'bottom-sheet', sm: 'regular' }}
    >
      <ModalTitle id="modal-title-echeck-adyen" sx={{ marginRight: '2rem' }}>
        Where do I find this?
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowEcheckModal(false)} />
      </ModalTitle>
      <ModalContent id="modal-content-echeck-adyen">
        <Typography variant="b1Regular">
          You can find your routing and account number at the bottom of your checks. The first set of numbers are the
          routing number, and the second set is your account number.
        </Typography>
        <div className="echeck-img-div">
          <img src={echeckImage} className="echeck-img" alt="echeck" />
        </div>
      </ModalContent>
      <ModalActions
        actionLayout={{ sm: 'fullWidth', md: 'fullWidth', lg: 'rightAlign', xl: 'rightAlign' }}
      >
        <ModalActionButton label="Got it" onClick={() => setShowEcheckModal(false)} />
      </ModalActions>
    </Modal>
  );

  const handleStorePaymentCheck = () => setStorePaymentChecked((sp) => !sp);

  const isScheduledPayment: boolean = !!isScheduled || !!manifest?.isScheduled;
  const authText = isScheduledPayment || !(amount > 0) || renderPaymentReview ? 'Continue' : 'Pay now';
  const isMap = appId === 'manage-autopay';
  const isStorePaymentFlow = amount === 0 && !autoPay;
  const mapChangeFlow = isMap && manifest?.renderTsAndCs?.render;
  const mapNewEnrollFlow = isMap && !manifest?.renderTsAndCs?.render;
  const renderTermsAndPaymentAuth = mapChangeFlow && paymentMethodsSkipped;
  const willVisitPaymentReview = renderPaymentReview && amount > 0;
  const renderPaymentAuth =
    !renderTermsAndPaymentAuth &&
    ((mapNewEnrollFlow && amount === 0) || mapChangeFlow || !(willVisitPaymentReview || isStorePaymentFlow));

  const handleBack = () => {
    if (paymentMethodsSkipped) {
      setpaymentMethodsSkipped(false);
      navigate(-2);
    } else navigate(-1);
  };

  const amountDue = amount > 0;
  const buttonText = !isScheduled && amountDue && !renderPaymentReview ? 'Pay now' : 'Continue';
  const disabled = !isFormValid || isLoadingSession;

  return (
    <>
      {isPaymentReview && !isProcessing && (
        <PaymentReview
          amount={manifest?.amount!}
          paymentDetails={{
            paymentType: PaymentType.Echeck,
            echeck: {
              last4,
              routingNumber,
            },
          }}
          scheduledDate={scheduledDate}
          pit={manifest?.pit ? { ...manifest.pit } : undefined}
          onSubmit={() => {
            if (reviewPayload) submitPayment(reviewPayload);
          }}
          onBack={() => setPaymentReview(false)}
        />
      )}

      <div
        className="payment-form"
        style={
          isLoadingSession || isProcessing || isPaymentReview || hideWhileParentProcessing
            ? { display: 'none' }
            : {}
        }
      >
        <div className="payment-form-cream-section">
          <div className="payment-form-body">
            <Header />
            {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={handleBack} />}
            <Box className="secure-checkout">
              <Icon iconName="lock-keyhole-solid" className="lock-icon" />
              <span>Secure checkout</span>
            </Box>
            <PaymentHeader paymentType={PaymentType.Echeck} amount={amount} autoPay={autoPay} />
          </div>
        </div>
        <div className="payment-form-body echeck-form">
          <Box id="echeck-form-adyen-inner-container">
            {achApiError && (
              <Alert severity="error" id="echeck-adyen-soft-error" displayCloseButton={false}>
                <AlertTypography>{achApiError}</AlertTypography>
              </Alert>
            )}
            <div id="ach-container" />
            {achRequiredPortalHosts.ownerName &&
              achRequiredTouched.ownerName &&
              achRequiredErrors.ownerName &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-custom-error="true"
                >
                  {achRequiredErrors.ownerName}
                </div>,
                achRequiredPortalHosts.ownerName,
              )}
            {achRequiredPortalHosts.routingNumber &&
              achRequiredTouched.routingNumber &&
              achRequiredErrors.routingNumber &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-custom-error="true"
                >
                  {achRequiredErrors.routingNumber}
                </div>,
                achRequiredPortalHosts.routingNumber,
              )}
            {achRequiredPortalHosts.accountNumber &&
              achRequiredTouched.accountNumber &&
              achRequiredErrors.accountNumber &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-custom-error="true"
                >
                  {achRequiredErrors.accountNumber}
                </div>,
                achRequiredPortalHosts.accountNumber,
              )}
            {achRequiredPortalHosts.accountNumberVerification &&
              achRequiredTouched.accountNumberVerification &&
              achRequiredErrors.accountNumberVerification &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-custom-error="true"
                >
                  {achRequiredErrors.accountNumberVerification}
                </div>,
                achRequiredPortalHosts.accountNumberVerification,
              )}
            {allowStoredPayment && (
              <CheckboxGroup className="save-this-payment" variant="text">
                <Checkbox
                  name="store-payment"
                  checked={storePaymentChecked}
                  label={<CheckboxTypography>Save for future payments</CheckboxTypography>}
                  enableForm={false}
                  onClick={() => handleStorePaymentCheck()}
                />
              </CheckboxGroup>
            )}
            {renderPaymentAuth && (
              <Typography variant="b1Regular" data-quid="payment-authorization-text">
                By clicking {authText}, I hereby state that I am an authorized user for the above payment method, and
                have read and agree to the{' '}
                <Link
                  id="payment-authorization-link"
                  component="button"
                  sx={{ verticalAlign: 'baseline' }}
                  onClick={(e: any) => {
                    setShowPaymentAuthorization(true);
                    e.preventDefault();
                  }}
                  variant="b1Regular"
                >
                  Payment Authorization
                </Link>
                .
              </Typography>
            )}
            {renderTermsAndPaymentAuth && (
              <Typography variant="b2Regular" className="terms-and-conditions">
                By clicking Continue, I hereby enroll in Auto Pay and that I am an authorized user for the above
                payment method I have selected, and have read and agree to the{' '}
                <Link
                  data-id="terms-conditions-link"
                  id="terms-conditions-link"
                  variant="b2Regular"
                  href={getTermsUrl()}
                  target="_blank"
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Terms & Conditions
                </Link>{' '}
                <Typography variant="b2Regular" sx={{ display: 'inline-block' }}>
                  and{' '}
                </Typography>
                <Link
                  data-id="terms-conditions-link"
                  variant="b2Regular"
                  component="button"
                  sx={{ verticalAlign: 'baseline' }}
                  onClick={(e: any) => {
                    setShowPaymentAuthorization(true);
                    e.preventDefault();
                  }}
                >
                  Payment Authorization
                </Link>
                .
              </Typography>
            )}
            {showPaymentAuthorization && (
              <PaymentMethodsAuthorizationModal
                handleClose={() => setShowPaymentAuthorization(false)}
                ctaText={authText}
              />
            )}
          </Box>
          <StickyButton
            id="submit-echeck-adyen"
            slotProps={{
              primaryButton: {
                label: buttonText,
                onClick: handleContinue,
                disabled,
                endIcon: amountDue ? <Icon iconName="lock-keyhole-solid" /> : undefined,
              },
            }}
            renderAmount={amountDue ? amount : undefined}
          />
        </div>
        {modalDialog}
        {echeckModal}
      </div>
    </>
  );
};

export default ECheckFormAdyen;
